import { ReactNode, useState, useEffect } from 'react';
import { cn } from '@/lib/utils/cn';

type SidebarLinkComponent = (props: {
  to: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
  title?: string;
}) => React.ReactElement;

export interface SidebarNavItem {
  label: string;
  href: string;
  icon: ReactNode;
  badge?: string;
  isActive?: boolean;
  children?: {
    label: string;
    href: string;
    badge?: string;
    isActive?: boolean;
  }[];
}

interface SidebarNavProps {
  items: SidebarNavItem[];
  adminItems?: SidebarNavItem[];
  adminLabel?: string;
  LinkComponent?: SidebarLinkComponent;
  onItemClick?: () => void;
  isExpanded?: boolean;
  className?: string;
  activeClassName: string;
  inactiveClassName: string;
  badgeClassName?: string;
}

function CollapsibleNavItem({
  item,
  LinkComponent,
  onItemClick,
  isExpanded,
  activeClassName,
  inactiveClassName,
  badgeClassName,
}: {
  item: SidebarNavItem;
  LinkComponent?: SidebarLinkComponent;
  onItemClick?: () => void;
  isExpanded: boolean;
  activeClassName: string;
  inactiveClassName: string;
  badgeClassName: string;
}) {
  const hasActiveChild = item.children?.some((child) => child.isActive);
  const [isOpen, setIsOpen] = useState(!!hasActiveChild);

  useEffect(() => {
    if (hasActiveChild) {
      setIsOpen(true);
    }
  }, [hasActiveChild]);

  const toggleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const isParentActive = !!hasActiveChild;

  const parentContent = (
    <>
      <span className="shrink-0">{item.icon}</span>
      <span
        className={cn(
          'transition-all duration-300 truncate',
          isExpanded ? 'lg:opacity-100 lg:w-auto' : 'lg:opacity-0 lg:w-0',
        )}
      >
        {item.label}
      </span>
      {isExpanded && (
        <span className="ml-auto flex items-center gap-1.5">
          {item.badge && (
            <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', badgeClassName)}>
              {item.badge}
            </span>
          )}
          <span className={cn(
            'material-symbols-outlined text-[18px] transition-transform duration-200',
            isOpen ? 'rotate-180' : ''
          )}>
            keyboard_arrow_down
          </span>
        </span>
      )}
    </>
  );

  const parentClassName = cn(
    'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap overflow-hidden cursor-pointer text-left',
    isParentActive ? activeClassName : inactiveClassName
  );

  return (
    <div className="space-y-1">
      <button
        type="button"
        className={parentClassName}
        onClick={toggleOpen}
        title={!isExpanded ? item.label : undefined}
      >
        {parentContent}
      </button>

      {isOpen && isExpanded && item.children && (
        <div className="space-y-1 pl-9 transition-all duration-200">
          {item.children.map((child, idx) => {
            const childClassName = cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-xs font-medium transition-colors whitespace-nowrap overflow-hidden',
              child.isActive
                ? 'bg-primary/10 text-primary-dark font-semibold border-l-2 border-primary pl-2.5 dark:text-primary-light'
                : 'text-slate-600 hover:bg-primary/5 dark:text-slate-400'
            );

            const childContent = (
              <>
                <span className="truncate">{child.label}</span>
                {child.badge && (
                  <span className={cn('ml-auto rounded-full px-2 py-0.5 text-[9px] font-bold', badgeClassName)}>
                    {child.badge}
                  </span>
                )}
              </>
            );

            if (LinkComponent) {
              return (
                <LinkComponent
                  key={`${child.href}-${idx}`}
                  to={child.href}
                  className={childClassName}
                  onClick={onItemClick}
                >
                  {childContent}
                </LinkComponent>
              );
            }

            return (
              <a
                key={`${child.href}-${idx}`}
                href={child.href}
                className={childClassName}
                onClick={onItemClick}
              >
                {childContent}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function SidebarNav({
  items,
  adminItems,
  adminLabel = 'Administration',
  LinkComponent,
  onItemClick,
  isExpanded = true,
  className,
  activeClassName,
  inactiveClassName,
  badgeClassName = 'bg-primary/10 text-primary',
}: SidebarNavProps) {
  const renderItem = (item: SidebarNavItem, key: string) => {
    if (item.children && item.children.length > 0) {
      return (
        <CollapsibleNavItem
          key={key}
          item={item}
          LinkComponent={LinkComponent}
          onItemClick={onItemClick}
          isExpanded={isExpanded}
          activeClassName={activeClassName}
          inactiveClassName={inactiveClassName}
          badgeClassName={badgeClassName}
        />
      );
    }

    const isActive = item.isActive;
    const itemClassName = cn(
      'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap overflow-hidden',
      isActive ? activeClassName : inactiveClassName,
    );

    const content = (
      <>
        <span className="shrink-0">{item.icon}</span>
        <span
          className={cn(
            'transition-all duration-300',
            isExpanded ? 'lg:opacity-100 lg:w-auto' : 'lg:opacity-0 lg:w-0',
          )}
        >
          {item.label}
        </span>
        {item.badge && (
          <span className={cn('ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold', badgeClassName)}>
            {item.badge}
          </span>
        )}
      </>
    );

    if (LinkComponent) {
      return (
        <LinkComponent
          key={key}
          to={item.href}
          className={itemClassName}
          onClick={onItemClick}
          title={!isExpanded ? item.label : undefined}
        >
          {content}
        </LinkComponent>
      );
    }

    return (
      <a
        key={key}
        href={item.href}
        className={itemClassName}
        onClick={onItemClick}
        title={!isExpanded ? item.label : undefined}
      >
        {content}
      </a>
    );
  };

  return (
    <nav className={cn('flex-1 space-y-1 px-4 pb-6', className)}>
      {items.map((item, index) => renderItem(item, `${item.href}-${item.label}-${index}`))}
      {adminItems && adminItems.length > 0 && (
        <>
          <div className="px-3 pt-6 text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400">
            {adminLabel}
          </div>
          {adminItems.map((item, index) => renderItem(item, `admin-${item.href}-${item.label}-${index}`))}
        </>
      )}
    </nav>
  );
}

